import { useCallback } from "react"

// Storage keys
const VIEW_KEY = "jobs-new-view"
const DAY_DISPLAY_MODE_KEY = "jobs-new-day-display-mode"
const MONTH_DISPLAY_MODE_KEY = "jobs-new-month-display-mode"

type View = "day" | "week" | "month"
type DisplayMode = "grid" | "list"

/**
 * Plain (non-hook) reader for the stored calendar view. Usable from route
 * loaders and prefetch helpers where hooks aren't available.
 */
export function getStoredCalendarView(): View | null {
  if (typeof window === "undefined") return null
  const stored = localStorage.getItem(VIEW_KEY)
  if (stored === "day" || stored === "week" || stored === "month") {
    return stored
  }
  return null
}

export function useCalendarPreferences() {
  const getStoredView = useCallback((): View | null => {
    return getStoredCalendarView()
  }, [])

  const getStoredDayDisplayMode = useCallback((): DisplayMode => {
    if (typeof window === "undefined") return "list"
    const stored = localStorage.getItem(DAY_DISPLAY_MODE_KEY)
    if (stored === "grid" || stored === "list") {
      return stored
    }
    return "list"
  }, [])

  const getStoredMonthDisplayMode = useCallback((): DisplayMode => {
    if (typeof window === "undefined") return "grid"
    const stored = localStorage.getItem(MONTH_DISPLAY_MODE_KEY)
    if (stored === "grid" || stored === "list") {
      return stored
    }
    return "grid"
  }, [])

  const setStoredView = useCallback((view: View) => {
    localStorage.setItem(VIEW_KEY, view)
  }, [])

  const setStoredDayDisplayMode = useCallback((mode: DisplayMode) => {
    localStorage.setItem(DAY_DISPLAY_MODE_KEY, mode)
  }, [])

  const setStoredMonthDisplayMode = useCallback((mode: DisplayMode) => {
    localStorage.setItem(MONTH_DISPLAY_MODE_KEY, mode)
  }, [])

  return {
    getStoredView,
    getStoredDayDisplayMode,
    getStoredMonthDisplayMode,
    setStoredView,
    setStoredDayDisplayMode,
    setStoredMonthDisplayMode,
  }
}
